/**
 * ProcurementSpendWidgets — Phase 102 — Home dashboard, Projects tab.
 *
 * Two compact horizontal-bar leaderboards reading straight from the same
 * purchase_requests collection Procurement uses (GET /procurement/spend-summary),
 * counting only "approved" spend. Click a row to see who/what it broke down into.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtINR } from "@/lib/finance";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ShoppingCart, Users2 } from "lucide-react";

const RANGE_OPTIONS = [
  { value: "month", label: "This month" },
  { value: "quarter", label: "This quarter" },
  { value: "all", label: "All time" },
];

export default function ProcurementSpendWidgets() {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/procurement/spend-summary", { params: { range } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Couldn't load spend data"); });
    return () => { alive = false; };
  }, [range]);

  return (
    <div className="card-flat overflow-hidden mt-4" data-testid="procurement-spend-widgets">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="font-display font-bold text-sm text-slate-900 inline-flex items-center gap-1.5">
          <ShoppingCart size={14} className="text-[#F54800]" /> Procurement spend
        </div>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="h-8 text-xs rounded-sm border border-slate-200 bg-white px-2"
          data-testid="procurement-spend-range"
        >
          {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {err ? (
        <div className="p-4 text-xs text-slate-500" data-testid="procurement-spend-error">{err}</div>
      ) : !data ? (
        <div className="p-4 text-xs text-slate-400 italic">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
          <SpendPanel
            title="Spend by project"
            emptyLabel="No approved project spend yet."
            hint="Only active projects are counted — closed/archived ones roll into Spend by person instead."
            rows={data.by_project}
            breakdownLabel="By team member"
            testId="spend-by-project"
          />
          <SpendPanel
            title="Spend by person"
            emptyLabel="No approved spend yet."
            rows={data.by_person}
            breakdownLabel="By project"
            testId="spend-by-person"
          />
        </div>
      )}
    </div>
  );
}

function SpendPanel({ title, emptyLabel, rows, breakdownLabel, testId, hint }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.total));
  return (
    <div className="p-4" data-testid={testId}>
      <div className="label-overline mb-2 inline-flex items-center gap-1.5"><Users2 size={11} /> {title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-slate-400 italic py-2">{emptyLabel}</div>
      ) : (
        <ul className="space-y-2.5">
          {rows.slice(0, 8).map((r) => (
            <SpendRow key={r.id || r.name} row={r} max={max} breakdownLabel={breakdownLabel} testId={testId} />
          ))}
        </ul>
      )}
      {hint && rows && rows.length > 0 && rows.length < 3 && (
        <div className="text-[10px] text-slate-400 mt-2.5 italic" data-testid={`${testId}-hint`}>{hint}</div>
      )}
    </div>
  );
}

function SpendRow({ row, max, breakdownLabel, testId }) {
  const pct = Math.max(4, Math.round((row.total / max) * 100));
  return (
    <li data-testid={`${testId}-row-${row.id || row.name}`}>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="w-full text-left group" data-testid={`${testId}-trigger-${row.id || row.name}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm text-slate-800 truncate group-hover:text-[#F54800] transition-colors">{row.name || "Unknown"}</span>
              <span className="text-xs font-mono font-semibold text-[#F54800] shrink-0">{fmtINR(row.total)}</span>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#F54800] transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 rounded-sm p-3 text-xs" data-testid={`${testId}-breakdown-${row.id || row.name}`}>
          <div className="font-semibold text-slate-700 mb-1.5">{breakdownLabel}</div>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {(row.breakdown || []).map((b, idx) => (
              <li key={b.id || idx} className="flex items-center justify-between gap-2">
                <span className="text-slate-600 truncate">{b.name || "Unknown"}</span>
                <span className="font-mono text-slate-800 shrink-0">{fmtINR(b.amount)}</span>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </li>
  );
}
